import React, { useMemo, useState } from 'react';

import DashboardDateRangePicker from '@features/dashboard/components/dashboard-date-range-picker/DashboardDateRangePicker';
import {
  DashboardDateRange,
  DashboardDateRangePickerTypesEnum
} from '@features/dashboard/constants';
import { getRollingYearStartDate } from '@utils/date';
import { DateParams } from 'types/lead-management';

import AgentsDistributionCard from './AgentsDistributionCard';
import LeadsDistributionCard from './LeadsDistributionCard';
import LeadStatusChartCard from './LeadStatusChartCard';
import TopLocationsCard from './TopLocationsCard';

import { dateRangeMap } from '../../constants';
import {
  EMPTY_DATE_RANGE,
  LeadAnalyticsCriteria
} from '../../constants/leadAnalytics';
import { toIsoRange } from '../../helper';
import useLeadAnalyticsData from '../../hooks/useLeadAnalyticsData';

const LeadAnalytics: React.FC = () => {
  const [pickerType, setPickerType] =
    useState<DashboardDateRangePickerTypesEnum>(
      DashboardDateRangePickerTypesEnum.thisMonth
    );
  const [pickerRange, setPickerRange] =
    useState<DashboardDateRange>(EMPTY_DATE_RANGE);
  const [locationView, setLocationView] = useState(
    LeadAnalyticsCriteria.CONVERSION
  );
  const [sourceView, setSourceView] = useState(
    LeadAnalyticsCriteria.CONVERSION
  );

  const dateParams: DateParams = useMemo(() => {
    const range = dateRangeMap[pickerType];
    if (pickerType === DashboardDateRangePickerTypesEnum.custom) {
      return { range, ...toIsoRange(pickerRange) };
    }
    return { range };
  }, [pickerType, pickerRange]);

  const {
    statusSeries,
    sourceItems,
    locations,
    agentRows,
    agentStatusList,
    isSalesAgent,
    loading
  } = useLeadAnalyticsData(dateParams, locationView, sourceView);

  const applyDateFilter = (
    dateRange: DashboardDateRange,
    dateRangeType: DashboardDateRangePickerTypesEnum
  ) => {
    setPickerType(dateRangeType);
    setPickerRange(dateRange);
  };

  const renderBody = () => (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-6 xl:flex-row">
        <TopLocationsCard
          locations={locations}
          view={locationView}
          onViewChange={setLocationView}
          isLoading={loading.locations}
        />
        <LeadsDistributionCard
          sources={sourceItems}
          view={sourceView}
          onViewChange={setSourceView}
          isLoading={loading.sources}
        />
      </div>

      <LeadStatusChartCard
        series={statusSeries}
        isLoading={loading.statusChart}
      />

      {!isSalesAgent && (
        <AgentsDistributionCard
          rows={agentRows}
          statusList={agentStatusList}
          isLoading={loading.agents}
        />
      )}
    </div>
  );

  return (
    <div className="flex w-full flex-col gap-6 py-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="text-xl font-semibold text-content">Analytics</div>
        <DashboardDateRangePicker
          minimumDateDifference={1}
          minDate={getRollingYearStartDate()}
          handleDateRangeSelect={applyDateFilter}
        />
      </div>
      {renderBody()}
    </div>
  );
};

export default LeadAnalytics;
